import { useState, useEffect, useRef, type FormEvent } from 'react';
import {
  Plus,
  Check,
  CalendarDays,
  CreditCard,
  Info,
  ArrowRight,
  ExternalLink,
  Trash2,
  Pencil,
  RotateCcw,
  Pause,
  Play,
  KeyRound,
  Smartphone,
  Copy,
} from 'lucide-react';
import { Modal, ServiceIcon, StatusBadge, TickProgress } from './UI';
import {
  categories,
  cycles,
  currencySymbols,
  todayString,
  nextPaymentDate,
  fullDate,
  money,
  addMonths,
  validateSubscription,
  type Subscription,
  type Payment,
  type AccessInfo,
} from '../lib/domain.ts';

export type Mutate = (
  path: string,
  method: string,
  body?: unknown,
  message?: string,
) => Promise<void>;
const colors = ['#47c8c0', '#6484de', '#eb8c45', '#db6172', '#9e86c7', '#20a58c', '#636d7b'];

export function SubscriptionForm({
  sub,
  today,
  onClose,
  mutate,
}: {
  sub?: Subscription;
  today: string;
  onClose: () => void;
  mutate: Mutate;
}) {
  const [form, setForm] = useState<Subscription>(
    sub || {
      id: '',
      name: '',
      provider: '',
      category: 'server',
      price: 0,
      currency: 'CNY',
      cycle: 'monthly',
      paymentDay: Number(today.slice(8)),
      startDate: today,
      nextPayment: addMonths(today, 1),
      expiryDate: '',
      autoRenew: false,
      remindDays: 7,
      status: 'active',
      resource: '',
      url: '',
      note: '',
      tags: [],
      color: colors[0],
      isDemo: false,
      createdAt: '',
      updatedAt: '',
    },
  );
  const [tagText, setTagText] = useState(form.tags.join('，'));
  const initialDraft = useRef(JSON.stringify({ form, tagText }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameRef.current?.focus();
  }, []);
  function field<K extends keyof Subscription>(key: K, value: Subscription[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const input = {
      ...form,
      tags: tagText
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean),
    };
    try {
      validateSubscription(input);
      setBusy(true);
      await mutate(
        sub ? `/subscriptions/${sub.id}` : '/subscriptions',
        sub ? 'PUT' : 'POST',
        input,
        sub ? '订阅已更新' : '订阅已添加',
      );
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={sub ? '编辑订阅' : '添加一份订阅'}
      eyebrow="KEEP EVERYTHING IN SIGHT"
      onClose={onClose}
      wide
      dirty={JSON.stringify({ form, tagText }) !== initialDraft.current}
      busy={busy}
    >
      <form onSubmit={submit} className="subscription-form">
        <div className="form-section-label">
          <CreditCard size={15} />
          基本信息
        </div>
        <div className="form-grid">
          <label className="span-2">
            订阅名称 <em>*</em>
            <input
              required
              ref={nameRef}
              maxLength={100}
              autoFocus
              value={form.name}
              onChange={(e) => field('name', e.target.value)}
              placeholder="例如：阿里云服务器、我的域名"
            />
          </label>
          <label>
            服务商
            <input
              value={form.provider}
              maxLength={100}
              onChange={(e) => field('provider', e.target.value)}
              placeholder="例如：Cloudflare"
            />
          </label>
          <label>
            分类
            <select
              value={form.category}
              onChange={(e) => field('category', e.target.value as Subscription['category'])}
            >
              {Object.entries(categories).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            每次付款金额 <em>*</em>
            <div className="input-combined">
              <select
                aria-label="币种"
                value={form.currency}
                onChange={(e) => field('currency', e.target.value as Subscription['currency'])}
              >
                {Object.keys(currencySymbols).map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <input
                aria-label="付款金额"
                type="number"
                min={0}
                max={100000000}
                step="0.01"
                required
                value={form.price}
                onChange={(e) => field('price', Number(e.target.value))}
              />
            </div>
          </label>
          <label>
            付款周期
            <select
              value={form.cycle}
              onChange={(e) => field('cycle', e.target.value as Subscription['cycle'])}
            >
              {Object.entries(cycles).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-section-label">
          <CalendarDays size={15} />
          日期与续费
        </div>
        <div className="form-grid">
          <label>
            开始日期 <em>*</em>
            <input
              type="date"
              aria-label="开始日期"
              required
              value={form.startDate}
              onInput={(e) => field('startDate', e.currentTarget.value)}
            />
          </label>
          <label>
            下次付款日期 <em>*</em>
            <input
              type="date"
              aria-label="下次付款日期"
              required={form.status === 'active'}
              min={form.startDate}
              value={form.nextPayment}
              onInput={(e) => {
                const value = e.currentTarget.value;
                setForm((f) => ({
                  ...f,
                  nextPayment: value,
                  paymentDay: Number(value.slice(8)) || f.paymentDay,
                }));
              }}
            />
          </label>
          <label>
            资源到期日期
            <input
              type="date"
              aria-label="资源到期日期"
              min={form.startDate}
              value={form.expiryDate}
              onInput={(e) => field('expiryDate', e.currentTarget.value)}
            />
            <small>可与付款日不同，长期有效可留空</small>
          </label>
          <label>
            固定付款日
            <div className="input-suffix">
              <input
                type="number"
                min={1}
                max={31}
                required
                value={form.paymentDay}
                onChange={(e) => field('paymentDay', Number(e.target.value))}
              />
              <span>号</span>
            </div>
            <small>31 号遇短月取月末，后续恢复 31 号</small>
          </label>
          <label>
            提前提醒
            <div className="input-suffix">
              <input
                type="number"
                min={0}
                max={365}
                required
                value={form.remindDays}
                onChange={(e) => field('remindDays', Number(e.target.value))}
              />
              <span>天</span>
            </div>
          </label>
          <div className="field-label">
            续费方式
            <div className="segmented form-segment" role="group" aria-label="续费方式">
              <button
                type="button"
                className={!form.autoRenew ? 'active' : ''}
                onClick={() => field('autoRenew', false)}
              >
                手动续费
              </button>
              <button
                type="button"
                className={form.autoRenew ? 'active' : ''}
                onClick={() => field('autoRenew', true)}
              >
                自动续费
              </button>
            </div>
          </div>
        </div>
        <div className="form-section-label">
          <Info size={15} />
          更多细节
        </div>
        <div className="form-grid">
          <label className="span-2">
            资源信息
            <input
              maxLength={300}
              value={form.resource}
              onChange={(e) => field('resource', e.target.value)}
              placeholder="域名、IP 地址、服务器区域或配置"
            />
          </label>
          <label>
            管理链接
            <input
              type="url"
              maxLength={1000}
              value={form.url}
              onChange={(e) => field('url', e.target.value)}
              placeholder="https://…"
            />
          </label>
          <label>
            标签
            <input
              value={tagText}
              onChange={(e) => setTagText(e.target.value)}
              placeholder="用逗号分隔，例如：生产环境，个人"
            />
          </label>
          <label className="span-2">
            备注
            <textarea
              rows={2}
              value={form.note}
              maxLength={3000}
              onChange={(e) => field('note', e.target.value)}
              placeholder="续费优惠、账号说明、注意事项…"
            />
          </label>
        </div>
        <div className="form-bottom-options">
          <div className="color-options">
            <span>卡片颜色</span>
            {colors.map((c) => (
              <button
                type="button"
                aria-label={`选择颜色 ${c}`}
                key={c}
                style={{ background: c }}
                className={form.color === c ? 'selected' : ''}
                onClick={() => field('color', c)}
              >
                {form.color === c && <Check size={13} />}
              </button>
            ))}
          </div>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.status === 'active'}
              onChange={(e) => field('status', e.target.checked ? 'active' : 'paused')}
            />
            启用订阅
          </label>
        </div>
        {error && (
          <div role="alert" className="form-error">
            {error}
          </div>
        )}
        <div className="modal-footer">
          <button className="button secondary" type="button" onClick={onClose}>
            取消
          </button>
          <button className="button primary" disabled={busy} type="submit">
            {sub ? <Check size={16} /> : <Plus size={16} />}
            {busy ? '保存中…' : sub ? '保存修改' : '添加订阅'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function PaymentForm({
  sub,
  today,
  onClose,
  mutate,
}: {
  sub: Subscription;
  today: string;
  onClose: () => void;
  mutate: Mutate;
}) {
  const [date, setDate] = useState(today),
    [periods, setPeriods] = useState(1),
    [amount, setAmount] = useState(sub.price),
    [extendExpiry, setExtendExpiry] = useState(!!sub.expiryDate),
    [note, setNote] = useState('');
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const months = cycles[sub.cycle].months * periods;
  const next = months ? addMonths(sub.nextPayment, months, sub.paymentDay) : '';
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await mutate(
        `/subscriptions/${sub.id}/pay`,
        'POST',
        {
          date,
          amount,
          periods,
          extendExpiry,
          note,
          expectedNextPayment: sub.nextPayment,
          expectedUpdatedAt: sub.updatedAt,
        },
        '付款已记录，日期已更新',
      );
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="记录一笔付款" eyebrow="PAYMENT RECORD" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="payment-service">
          <ServiceIcon sub={sub} />
          <div>
            <h3>{sub.name}</h3>
            <p>
              {money(sub.price, sub.currency)} / {cycles[sub.cycle].short}
            </p>
          </div>
        </div>
        <div className="info-callout">
          <Info size={16} />
          <span>记录已完成的付款。自动续费标记用于提醒，应用不会实际扣款。</span>
        </div>
        <div className="form-grid">
          <label>
            实际付款日期
            <input
              required
              type="date"
              min={sub.startDate}
              max={today}
              value={date}
              onInput={(e) => setDate(e.currentTarget.value)}
            />
          </label>
          <label>
            实际金额（{sub.currency}）
            <input
              type="number"
              required
              min={0}
              max={100000000}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </label>
          {months > 0 && (
            <label className="span-2">
              支付几个周期
              <select
                value={periods}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setPeriods(value);
                  setAmount(Math.round(sub.price * value * 100) / 100);
                }}
              >
                {[1, 2, 3, 6, 12, 24].map((n) => (
                  <option value={n} key={n}>
                    {n} 个周期 · {cycles[sub.cycle].months * n} 个月
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="span-2">
            付款备注
            <input
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：支付宝、续费优惠"
            />
          </label>
        </div>
        <div className="date-advance">
          <span>下次付款</span>
          <b>{fullDate(sub.nextPayment)}</b>
          <ArrowRight size={16} />
          <strong>{next ? fullDate(next) : '一次性付款完成'}</strong>
        </div>
        {sub.expiryDate && months > 0 && (
          <label className="checkbox-label expiry-checkbox">
            <input
              type="checkbox"
              checked={extendExpiry}
              onChange={(e) => setExtendExpiry(e.target.checked)}
            />
            同时延长资源到期日期 {cycles[sub.cycle].months * periods} 个月
          </label>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="modal-footer">
          <button className="button secondary" type="button" onClick={onClose}>
            取消
          </button>
          <button className="button primary" type="submit" disabled={busy}>
            <Check size={16} />
            {busy ? '记录中…' : '确认已付款'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function DetailModal({
  sub,
  payments,
  today,
  onClose,
  onEdit,
  onPay,
  mutate,
  confirm,
}: {
  sub: Subscription;
  payments: Payment[];
  today: string;
  onClose: () => void;
  onEdit: () => void;
  onPay: () => void;
  mutate: Mutate;
  confirm: (title: string, text: string, action: () => Promise<void>) => void;
}) {
  const receipts = payments.filter((p) => p.subscriptionId === sub.id);
  return (
    <Modal title="订阅详情" onClose={onClose}>
      <div className="detail-hero">
        <ServiceIcon sub={sub} />
        <div>
          <h3>{sub.name}</h3>
          <p>
            {sub.provider || categories[sub.category].label}{' '}
            {sub.isDemo && <span className="demo-tag">示例</span>}
          </p>
        </div>
        <StatusBadge sub={sub} today={today} />
      </div>
      <div className="detail-price">
        {money(sub.price, sub.currency)}
        <span> / {cycles[sub.cycle].short}</span>
      </div>
      <TickProgress sub={sub} today={today} />
      <div className="detail-grid">
        {[
          ['开始日期', fullDate(sub.startDate)],
          ['下次付款', fullDate(sub.nextPayment)],
          ['资源到期', sub.expiryDate ? fullDate(sub.expiryDate) : '长期有效 / 未设置'],
          ['固定付款日', `${sub.paymentDay} 号`],
          ['续费方式', sub.autoRenew ? '自动续费' : '手动续费'],
          ['提前提醒', `${sub.remindDays} 天`],
        ].map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <b>{value}</b>
          </div>
        ))}
      </div>
      {sub.resource && (
        <div className="detail-section">
          <span>资源信息</span>
          <p>{sub.resource}</p>
        </div>
      )}
      {sub.tags.length > 0 && (
        <div className="tag-list">
          {sub.tags.map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
      )}
      {sub.note && (
        <div className="detail-section">
          <span>备注</span>
          <p className="preserve-lines">{sub.note}</p>
        </div>
      )}
      {sub.url && (
        <a href={sub.url} target="_blank" rel="noreferrer" className="manage-link">
          前往服务商管理
          <ExternalLink size={14} />
        </a>
      )}
      <div className="detail-section payment-history">
        <span>
          付款记录 <small>{receipts.length} 笔</small>
        </span>
        {receipts.length ? (
          receipts.slice(0, 6).map((p, i) => (
            <div className="history-row" key={p.id}>
              <div>
                <b>{money(p.amount, p.currency)}</b>
                <small>
                  {fullDate(p.date)} {p.note && `· ${p.note}`}
                </small>
              </div>
              {i === 0 && (
                <button
                  className="text-button"
                  onClick={() =>
                    confirm('撤销这笔付款？', '撤销后将恢复付款前的日期和状态。', async () => {
                      await mutate(`/payments/${p.id}/undo`, 'POST', {}, '付款记录已撤销');
                      onClose();
                    })
                  }
                >
                  <RotateCcw size={13} />
                  撤销
                </button>
              )}
            </div>
          ))
        ) : (
          <p className="muted">尚未记录付款，确认续费后会显示在这里。</p>
        )}
      </div>
      <div className="detail-actions">
        <button className="button secondary" onClick={onEdit}>
          <Pencil size={15} />
          编辑
        </button>
        <button
          className="button secondary"
          onClick={() =>
            confirm(
              sub.status === 'active' ? '停用这份订阅？' : '重新启用订阅？',
              sub.status === 'active'
                ? '停用后保留全部记录，费用统计和提醒将不再包含它。'
                : '启用后将重新计入统计和到期提醒，请检查付款日期。',
              async () => {
                await mutate(
                  `/subscriptions/${sub.id}`,
                  'PUT',
                  { ...sub, status: sub.status === 'active' ? 'paused' : 'active' },
                  '订阅状态已更新',
                );
                onClose();
              },
            )
          }
        >
          {sub.status === 'active' ? <Pause size={15} /> : <Play size={15} />}
          {sub.status === 'active' ? '停用' : '启用'}
        </button>
        {sub.status === 'active' && (
          <button className="button primary" onClick={onPay}>
            <Check size={15} />
            记录付款
          </button>
        )}
        <button
          className="icon-button danger"
          aria-label="删除订阅"
          onClick={() =>
            confirm(
              '删除这份订阅？',
              '订阅和付款记录将被删除。系统会先在本机保存一份备份。',
              async () => {
                await mutate(
                  `/subscriptions/${sub.id}`,
                  'DELETE',
                  undefined,
                  '订阅已删除，本机已保存备份',
                );
                onClose();
              },
            )
          }
        >
          <Trash2 size={17} />
        </button>
      </div>
    </Modal>
  );
}

export function AccessModal({
  access,
  onClose,
  mutate,
  notify,
}: {
  access: AccessInfo;
  onClose: () => void;
  mutate: Mutate;
  notify: (s: string) => void;
}) {
  const [password, setPassword] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const online = access.deployment === 'server';
  const needsCurrentPassword = access.passwordEnabled && !access.isLocal;
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirmation) {
      setError('两次输入的新密码不一致');
      return;
    }
    setBusy(true);
    try {
      await mutate(
        '/password',
        'POST',
        { password, currentPassword },
        '访问密码已更新，其他设备需重新登录',
      );
      setPassword('');
      setConfirmation('');
      setCurrentPassword('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notify('地址已复制');
    } catch {
      notify('请手动复制下方地址');
    }
  }
  return (
    <Modal
      title={online ? '跨设备访问' : '在手机上接着管理'}
      eyebrow={online ? 'ONE WORKSPACE, EVERYWHERE' : 'SAME WI-FI, SAME WORKSPACE'}
      onClose={onClose}
      dirty={!!(password || confirmation || currentPassword)}
      busy={busy}
    >
      <div className="access-illustration">
        <Smartphone size={40} />
        <span>
          {online ? '手机 · 电脑' : '同一 Wi-Fi'}
          <br />
          {online ? '共享服务器数据' : '共享本机数据'}
        </span>
      </div>
      <div className="access-steps">
        <p>
          <b>01</b>{' '}
          {online
            ? '用任意联网设备打开下方网址，无需同一 Wi-Fi。'
            : '手机和这台电脑连接同一个 Wi-Fi。'}
        </p>
        <p>
          <b>02</b>{' '}
          {online
            ? '输入同一个访问密码，进入同一工作空间。'
            : '设置访问密码，再用手机浏览器打开下方地址。'}
        </p>
        <p>
          <b>03</b>{' '}
          {online
            ? '保存后其他设备自动同步；电脑关机也能继续使用。可添加到手机主屏幕。'
            : '登录后即可管理；电脑需要保持应用运行。'}
        </p>
      </div>
      {access.addresses.length ? (
        access.addresses.map((address) => (
          <div className="network-address" key={address}>
            <code>{address}</code>
            <button
              className="icon-button"
              aria-label="复制手机访问地址"
              onClick={() => copy(address)}
            >
              <Copy size={16} />
            </button>
          </div>
        ))
      ) : (
        <div className="info-callout">当前没有找到局域网地址，请连接 Wi-Fi 后刷新。</div>
      )}
      <form onSubmit={submit}>
        {needsCurrentPassword && (
          <label className="password-label">
            当前访问密码
            <input
              type="password"
              name="currentPassword"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="先验证当前密码"
            />
          </label>
        )}
        <label className="password-label">
          {access.passwordEnabled ? '更换访问密码' : '设置访问密码'}
          <div className="input-with-icon">
            <KeyRound size={16} />
            <input
              type="password"
              name="newPassword"
              autoComplete="new-password"
              minLength={6}
              maxLength={200}
              required
              placeholder="至少 6 个字符"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </label>
        <label className="password-label">
          再次输入新密码
          <input
            type="password"
            name="confirmPassword"
            autoComplete="new-password"
            required
            minLength={6}
            maxLength={200}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="与上方新密码一致"
          />
        </label>
        {access.passwordEnabled && (
          <p className="success-text">
            <Check size={14} />
            已启用密码保护。更换密码后其他设备需重新登录。
          </p>
        )}
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <div className="modal-footer">
          <button className="button secondary" type="button" onClick={onClose}>
            完成
          </button>
          <button className="button primary" type="submit" disabled={busy}>
            {busy ? '设置中…' : '保存访问密码'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ConfirmModal({
  title,
  text,
  onClose,
  action,
}: {
  title: string;
  text: string;
  onClose: () => void;
  action: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  return (
    <Modal title={title} onClose={onClose}>
      <p className="confirm-text">{text}</p>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="modal-footer">
        <button className="button secondary" onClick={onClose}>
          取消
        </button>
        <button
          className="button primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await action();
              onClose();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? '处理中…' : '确认'}
        </button>
      </div>
    </Modal>
  );
}
